// Sugalarm — phone-sized frame variant (cycle 25).
//
// Lives next to KioskFrame.tsx; KioskFrame routes to this component
// when `variant === "mobile"`. The header is a compact 52 px strip, the
// content area scrolls, and the six-tab nav lives at the bottom with
// `env(safe-area-inset-bottom)` so iOS home indicator doesn't crash
// into the icons. No volume button (no Pi-local agent), no
// OnScreenKeyboard (the phone has its own), no EdgeRail, no
// TestAlarmDriver — those are kiosk affordances.
//
// Cycle 114: in LANDSCAPE the bottom tab bar relocates to a slim
// vertical icon rail on the LEFT edge (app-wide) so it stops eating the
// scarce ~390 px of vertical space, and the content area sits to its
// right. Same items/order/routes/glyphs/active accent — a layout
// relocation, not a redesign. The compact header is additionally hidden
// on the Now screen in landscape (the Now split-pane's stats column
// carries the glance info); every other screen keeps the header.
// Orientation is read through the single-owner `useIsLandscape` hook.
// Portrait is byte-identical to cycle 112.

import { type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useIsLandscape } from "../../hooks/useIsLandscape";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import { usePhotoCaptureAction } from "../../hooks/usePhotoCaptureAction";
import { usePhotoEnabled } from "../../hooks/usePhotoEnabled";
import type { TTSState } from "../../hooks/useTTS";
import { enterClockMode } from "../../lib/clockMode";
import { hapticSelect } from "../../lib/haptics";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import type { User, Zone } from "../../lib/types";
import { MemberAvatar } from "../ui/MemberAvatar";
import { SpeakingBanner } from "../voice/SpeakingBanner";
import { WakeBanner } from "../voice/WakeBanner";
import { MobileBackground } from "./MobileBackground";
import styles from "./MobileFrame.module.css";
import { MobileHeader } from "./MobileHeader";
import { NAV_ITEMS, NavIcon, type NavId } from "./NavIcon";

// Cycle 341: the Settings tab's glyph — design-audit "Option A" (avatar +
// gear badge). Personalization at a glance (which household member is
// active) without losing the unambiguous settings affordance the bare
// avatar alone would risk: the avatar already means "active household
// member" elsewhere in the app (Household, Settings → Members lets any
// member's photo become "the" avatar shown app-wide), so a bare photo in
// primary nav could read as "switch member" rather than "open settings" —
// the small gear badge is what keeps that legible, including mid-alert.
// The badge reuses the exact cycle-78.1 six-tooth cog path (NavIcon
// "settings") at a smaller scale, never a second glyph design.
const SETTINGS_GEAR_PATH =
  "M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.281Z";

function SettingsTabGlyph({
  avatarId,
  name,
  size,
  active,
}: {
  avatarId: string | null;
  name: string;
  size: number;
  active: boolean;
}) {
  const badge = Math.round(size * 0.42);
  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <MemberAvatar avatarId={avatarId} name={name} size={size} shape="circle" />
      {active && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: -1.5,
            borderRadius: "50%",
            boxShadow: "0 0 0 1.5px var(--accent)",
            pointerEvents: "none",
          }}
        />
      )}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          right: -2,
          bottom: -2,
          width: badge,
          height: badge,
          borderRadius: "50%",
          background: "var(--ink-2)",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1.5px solid var(--bg-elev)",
        }}
      >
        {/* Filled silhouette, not the sibling NavIcon's stroked outline —
            a thin stroke disappears at this ~5px badge scale, a solid
            shape doesn't. */}
        <svg
          width={badge * 0.6}
          height={badge * 0.6}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d={SETTINGS_GEAR_PATH} />
        </svg>
      </span>
    </span>
  );
}

// The main mobile menu's left-to-right order, including the two appended
// ACTION items (Camera, Clock — neither is a routed tab). Camera sits in
// the middle of the row (between Log and Household); Clock stays right
// before Settings (cycle 146). Drives the `order` CSS property on both the
// portrait tab bar and the landscape rail so the two layouts can't drift.
const MOBILE_NAV_ORDER: Record<NavId, number> = {
  now: 0,
  history: 1,
  log: 2,
  camera: 3,
  household: 4,
  clock: 5,
  settings: 6,
};

interface Props {
  user: User | null;
  socket: GlucoseSocketState;
  now: number;
  units: "mg/dL" | "mmol/L";
  ageMin: number;
  /** Cycle 435: the selector-released current value for the header, or
   *  null (withheld). Computed once in KioskFrame; a raw `Reading` cannot
   *  be passed here. */
  current: DisplayGlucose | null;
  zone: Zone;
  memberName: string | null;
  /** Cycle 105: active member's avatar id (null → Sugalarm shield). */
  memberAvatarId?: string | null;
  showChrome: boolean;
  /** Cycle 267: render as a centered, phone-width column (non-official
   *  iPad / Android tablet) — constrains the root width + forces the
   *  portrait layout so the app keeps the mobile-phone look-and-feel
   *  instead of stretching edge-to-edge. */
  tabletCentered?: boolean;
  predictionPill?: string | null;
  /** Cycle 97: operator broadcast banner, rendered between the header and
   *  the scrolling content so it reserves height (never an overlay). */
  banner?: ReactNode;
  /** Cycle 222: the app-wide TTS handle + episode-level urgent — drive the
   *  transient bottom SpeakingBanner (mobile PORTRAIT only). */
  tts?: TTSState;
  urgentActive?: boolean;
  /** Cycle 383 — a hands-free voice command session is capturing or
   *  thinking. Drives the SAME `WakeBanner` the kiosk uses (no new visual
   *  component). Android phone app only; false everywhere else. */
  voiceListening?: boolean;
  children: ReactNode;
}

const TAB_BAR_HEIGHT = 56;
// Cycle 115: landscape left-rail content width (after the safe-area
// padding). 64 px is a comfortable icon + label touch column.
const RAIL_WIDTH = 64;

export function MobileFrame({
  user,
  socket,
  now,
  units,
  ageMin,
  current,
  zone,
  memberName,
  memberAvatarId = null,
  showChrome,
  tabletCentered = false,
  predictionPill,
  banner = null,
  tts,
  urgentActive = false,
  voiceListening = false,
  children,
}: Props) {
  // Cycle 306: the header provider pill reflects the ACTIVE MEMBER's
  // connected CGM (member-scoped, fanned out via `member_update` on
  // connect/disconnect) rather than the last reading's provider, which
  // lagged a Libre → Dexcom switch until the first new-sensor reading.
  // Same `dashboardCards` source the cycle-116 member-count logic uses.
  const activeCgmProvider =
    socket.dashboardCards.find((c) => c.member_id === socket.activeMemberId)
      ?.cgm_provider ?? null;
  // Cycle 114: single-owner orientation read (governs layout within the
  // mobile variant only — never variant resolution).
  // Cycle 267.1: when rendered inside the phone-sized box on a non-official
  // tablet (`tabletCentered`), we keep the REAL orientation so the box +
  // layout rotate together — portrait box → portrait layout, landscape box
  // → landscape layout, just like a real phone.
  const isLandscape = useIsLandscape();
  const location = useLocation();
  // Camera ACTION item — take a photo of food straight into the Log, from
  // the main mobile menu. Direct-camera capture (no kiosk chooser — the
  // mobile frame is never the device-owner kiosk shell), shown once the
  // server confirms the photo feature is configured.
  const photoEnabled = usePhotoEnabled();
  const photoCapture = usePhotoCaptureAction(false);
  // Cycle 116: the portrait Now route gets a large-title header (bold
  // name + avatar, no mini reading / provider pill — the hero below
  // carries the value), and the whole identity row is HIDDEN when only
  // one member is accessible. Member count + self-membership come from
  // the same accessible-set the active-member switcher uses — the WS
  // snapshot's `dashboardCards` (owned ∪ granted) already on `socket`.
  const isNowRoute =
    location.pathname === "/now" || location.pathname === "/";
  const memberCount = socket.dashboardCards.length;
  const isSelfMember =
    socket.dashboardCards.find((c) => c.member_id === socket.activeMemberId)
      ?.is_self ?? false;
  // Cycle 117: the portrait Now route ALWAYS renders the large-title
  // identity header (the cycle-116 single-member hiding rule is removed
  // per operator direction — it was firing on single-member accounts and
  // leaving the screen headerless). The `you` affix still only shows when
  // the active member is the self-member AND >1 member is accessible.
  const nowTitleMode = showChrome && !isLandscape && isNowRoute;
  // Cycle 115: header hidden in landscape on all screens; portrait keeps
  // it on every route.
  const headerVisible = showChrome && !isLandscape;

  const header = headerVisible ? (
    <MobileHeader
      user={user}
      current={current}
      cgmProvider={activeCgmProvider}
      now={now}
      units={units}
      ageMin={ageMin}
      zone={zone}
      memberName={memberName}
      memberAvatarId={memberAvatarId}
      predictionPill={predictionPill ?? null}
      nowTitle={nowTitleMode}
      isSelfMember={isSelfMember && memberCount > 1}
    />
  ) : null;

  // Portrait bottom tab bar (cycle 25, unchanged — only rendered in
  // portrait now; landscape relocates it to the left rail below).
  const tabBar =
    showChrome && !isLandscape ? (
      <div
        // Cycle 165: frosted glass over the aurora — fill/sheen via the
        // --glass-* tokens; layout, tabs, routing, glyphs, active accent,
        // and the safe-area-inset-bottom padding all unchanged.
        className={styles.glassChrome}
        style={{
          flexShrink: 0,
          borderTop: "0.5px solid var(--glass-border)",
          paddingBottom: "env(safe-area-inset-bottom, 0)",
        }}
      >
        <div
          style={{
            height: TAB_BAR_HEIGHT,
            display: "grid",
            // 5 routed tabs + the appended Clock action item + (once the
            // server confirms it) the appended Camera action item.
            gridTemplateColumns: `repeat(${5 + 1 + (photoEnabled ? 1 : 0)}, 1fr)`,
          }}
        >
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.id}
              to={n.path}
              style={({ isActive }) => ({
                appearance: "none",
                border: 0,
                background: "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                position: "relative",
                color: isActive ? "var(--accent)" : "var(--ink-3)",
                textDecoration: "none",
                fontWeight: 600,
                fontSize: "var(--type-floor)",
                minHeight: TAB_BAR_HEIGHT,
                order: MOBILE_NAV_ORDER[n.id],
              })}
            >
              {({ isActive }) => (
                <>
                  {n.id === "settings" ? (
                    <SettingsTabGlyph
                      avatarId={memberAvatarId}
                      name={memberName ?? user?.display_name ?? "Sugar Alarm"}
                      size={22}
                      active={isActive}
                    />
                  ) : (
                    <NavIcon id={n.id} size={20} active={isActive} />
                  )}
                  <span style={{ letterSpacing: "0.01em" }}>{n.label}</span>
                  {isActive && (
                    <span
                      style={{
                        position: "absolute",
                        top: 0,
                        left: "30%",
                        right: "30%",
                        height: 2,
                        background: "var(--accent)",
                        borderRadius: 999,
                      }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
          {/* Camera ACTION item — sits in the middle of the row (between
              Log and Household); hands a fresh photo straight to
              /log/photo. Not a routed tab, never shows the active
              treatment. */}
          {photoEnabled && (
            <>
              <input
                ref={photoCapture.inputRef}
                type="file"
                accept={photoCapture.accept}
                capture={photoCapture.capture}
                onChange={photoCapture.onChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                aria-label="Photo of meal"
                onClick={() => {
                  hapticSelect();
                  photoCapture.open();
                }}
                style={{
                  appearance: "none",
                  border: 0,
                  background: "transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  color: "var(--ink-3)",
                  fontFamily: "inherit",
                  fontWeight: 600,
                  fontSize: "var(--type-floor)",
                  minHeight: TAB_BAR_HEIGHT,
                  cursor: "pointer",
                  order: MOBILE_NAV_ORDER.camera,
                }}
              >
                <NavIcon id="camera" size={20} />
                <span style={{ letterSpacing: "0.01em" }}>Photo</span>
              </button>
            </>
          )}
          {/* Cycle 145: Clock ACTION item — enters alarm-clock mode;
              not a routed tab, never shows the active treatment. */}
          <button
            type="button"
            aria-label="Alarm clock mode"
            onClick={() => {
              hapticSelect();
              enterClockMode("nav", { priorPath: location.pathname });
            }}
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              color: "var(--ink-3)",
              fontFamily: "inherit",
              fontWeight: 600,
              fontSize: "var(--type-floor)",
              minHeight: TAB_BAR_HEIGHT,
              cursor: "pointer",
              order: MOBILE_NAV_ORDER.clock,
            }}
          >
            <NavIcon id="clock" size={20} />
            <span style={{ letterSpacing: "0.01em" }}>Clock</span>
          </button>
        </div>
      </div>
    ) : null;

  // Cycle 115: landscape left nav rail — a detached edge-flush PANEL
  // (the cycle-103/111 EdgeRail aesthetic): translucent fill + blur,
  // flush to the left screen edge with rounded right corners, a hairline
  // on the visible (top/right/bottom) edges only, and NO vertical
  // separator bisecting the labels. The outer column carries
  // `padding-left: env(safe-area-inset-left)` so the panel clears the
  // notch/camera housing when rotated notch-left, and the panel carries
  // `padding-bottom: env(safe-area-inset-bottom)` so the last item clears
  // the home indicator. Each item is icon-over-label (label ≈ 9.5px,
  // never clipping); the active item gets a filled accent-soft pill
  // behind the icon. "Household" shortens to "House" at this size.
  const railLabel = (id: string, label: string) =>
    id === "household" ? "House" : label;
  const rail =
    showChrome && isLandscape ? (
      <div
        style={{
          flexShrink: 0,
          height: "100%",
          paddingLeft: "env(safe-area-inset-left, 0)",
          display: "flex",
        }}
      >
        <div
          // Cycle 169: the rail joins the mobile frosted-glass family
          // (the cycle-165 tab-bar treatment). It previously fell back to
          // a theme-NEUTRAL translucent BLACK fill
          // (`--kiosk-nav-fill` only exists inside `.kiosk`, so on mobile
          // it always resolved to the rgba(0,0,0,0.55) inline fallback),
          // which left the slate idle labels illegible on the LIGHT
          // aurora ground. `.glassChrome` flips Light↔Dark with the
          // --glass-* tokens (and carries the no-blur @supports solid
          // fallback); the idle labels read --ink-3 slate, the active
          // item --accent. Layout / items / order / glyphs unchanged.
          className={styles.glassChrome}
          style={{
            width: RAIL_WIDTH,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderTop: "0.5px solid var(--glass-border)",
            borderRight: "0.5px solid var(--glass-border)",
            borderBottom: "0.5px solid var(--glass-border)",
            paddingBottom: "env(safe-area-inset-bottom, 0)",
          }}
        >
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.id}
              to={n.path}
              aria-label={n.label}
              onClick={() => hapticSelect()}
              style={({ isActive }) => ({
                appearance: "none",
                border: 0,
                background: "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                flex: 1,
                minHeight: 0,
                color: isActive ? "var(--accent)" : "var(--ink-3)",
                textDecoration: "none",
                fontWeight: 600,
                fontSize: "var(--type-floor)",
                order: MOBILE_NAV_ORDER[n.id],
              })}
            >
              {({ isActive }) => (
                <>
                  {n.id === "settings" ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 34,
                        height: 30,
                      }}
                    >
                      <SettingsTabGlyph
                        avatarId={memberAvatarId}
                        name={memberName ?? user?.display_name ?? "Sugar Alarm"}
                        size={24}
                        active={isActive}
                      />
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 34,
                        height: 30,
                        borderRadius: 999,
                        background: isActive ? "var(--accent-soft)" : "transparent",
                      }}
                    >
                      <NavIcon id={n.id} size={20} active={isActive} />
                    </span>
                  )}
                  <span style={{ letterSpacing: "0.01em" }}>
                    {railLabel(n.id, n.label)}
                  </span>
                </>
              )}
            </NavLink>
          ))}
          {/* Camera ACTION item — sits in the middle of the rail (between
              Log and Household); hands a fresh photo straight to
              /log/photo. */}
          {photoEnabled && (
            <>
              <input
                ref={photoCapture.inputRef}
                type="file"
                accept={photoCapture.accept}
                capture={photoCapture.capture}
                onChange={photoCapture.onChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                aria-label="Photo of meal"
                onClick={() => {
                  hapticSelect();
                  photoCapture.open();
                }}
                style={{
                  appearance: "none",
                  border: 0,
                  background: "transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  flex: 1,
                  minHeight: 0,
                  color: "var(--ink-3)",
                  fontFamily: "inherit",
                  fontWeight: 600,
                  fontSize: "var(--type-floor)",
                  cursor: "pointer",
                  order: MOBILE_NAV_ORDER.camera,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 30,
                  }}
                >
                  <NavIcon id="camera" size={20} />
                </span>
                <span style={{ letterSpacing: "0.01em" }}>Photo</span>
              </button>
            </>
          )}
          {/* Cycle 145: Clock ACTION item (enters alarm-clock mode). */}
          <button
            type="button"
            aria-label="Alarm clock mode"
            onClick={() => {
              hapticSelect();
              enterClockMode("nav", { priorPath: location.pathname });
            }}
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              flex: 1,
              minHeight: 0,
              color: "var(--ink-3)",
              fontFamily: "inherit",
              fontWeight: 600,
              fontSize: "var(--type-floor)",
              cursor: "pointer",
              order: MOBILE_NAV_ORDER.clock,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 34,
                height: 30,
              }}
            >
              <NavIcon id="clock" size={20} />
            </span>
            <span style={{ letterSpacing: "0.01em" }}>Clock</span>
          </button>
        </div>
      </div>
    ) : null;

  // ── Landscape: [rail | content column] horizontal split (app-wide) ──
  if (showChrome && isLandscape) {
    return (
      <div data-overlay-host=""
        style={{
          position: "relative",
          // Cycle 164: own stacking context so the aurora's z-index -1
          // stays inside this frame (never escapes to an ancestor).
          isolation: "isolate",
          // Cycle 267.1: fill the phone-box on a non-official tablet (see
          // the portrait root); 100vh on phones (byte-identical).
          height: tabletCentered ? "100%" : "100vh",
          display: "flex",
          flexDirection: "row",
          background: "var(--bg)",
          overflow: "hidden",
          paddingTop: "env(safe-area-inset-top, 0)",
        }}
      >
        {/* Cycle 164 → 171: the backmost app-background layer. Defaults to
            the cycle-164 aurora; renders the user-picked preset otherwise. */}
        <MobileBackground />
        {rail}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            paddingRight: "env(safe-area-inset-right, 0)",
          }}
        >
          {header}
          {banner}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {children}
          </div>
        </div>
        {/* Cycle 214: SPEAKING VoiceBanner removed — the Pulse-Core orb on
            the Now hero is the sole speaking indicator app-wide. */}
      </div>
    );
  }

  // ── Portrait (and the no-chrome login fall-through): unchanged ──
  return (
    <div data-overlay-host=""
      style={{
        position: "relative",
        // Cycle 164: own stacking context for the aurora layer.
        isolation: "isolate",
        // Cycle 267.1: inside the phone-sized box on a non-official tablet,
        // fill the box (100%) instead of the viewport (100vh); the
        // `.phone-box` host owns the phone dimensions + rounding + shadow.
        // Phones (no `tabletCentered`) are byte-identical at 100vh.
        height: tabletCentered ? "100%" : "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
        overflow: "hidden",
        paddingTop: "env(safe-area-inset-top, 0)",
      }}
    >
      {/* Cycle 164 → 171: the backmost app-background layer. Defaults to
          the cycle-164 aurora; renders the user-picked preset otherwise. */}
      <MobileBackground />
      {header}
      {/* Cycle 97: broadcast banner reserves height below the header. */}
      {banner}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </div>
      {tabBar}
      {/* Cycle 222: transient bottom "speaking" banner — PORTRAIT only (this
          is the portrait return; the landscape split-pane above never mounts
          it, per the cycle-214 scope decision). Clears the TabBar + iOS
          home-indicator safe-area. Gated on chrome so it never paints over
          the clock face / login. */}
      {showChrome && tts ? (
        <SpeakingBanner
          tts={tts}
          zone={zone}
          urgentActive={urgentActive}
          variant="mobile"
          bottom="calc(56px + env(safe-area-inset-bottom, 0px))"
          left={0}
        />
      ) : null}
      {/* Cycle 383: the hands-free voice listening banner — the SAME
          cycle-100 WakeBanner/Waveform chrome the kiosk mounts, so no new
          visual component enters the mobile frame. It overlays (absolute,
          anchored to this positioned root) and sits above the 56 px tab bar
          + home indicator, so it never displaces or overflows content.
          `active` is only passed while a session is running; with the dev
          flag unset `voiceListening` is permanently false and this renders
          null. Gated on chrome so it never paints over the clock face. */}
      {showChrome && voiceListening ? (
        <WakeBanner lastWakeTs={null} variant="mobile" active />
      ) : null}
    </div>
  );
}
